import axios from 'axios';
import { ApiResponse } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
    baseURL: API_BASE_URL,
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

export interface DocumentMetadata {
    id: number;
    user_id: number;
    document_type: string;
    title: string;
    description?: string;
    filename: string;
    original_filename: string;
    file_path: string;
    file_size: number;
    mime_type: string;
    uploaded_at: string;
    tags?: string[];
    access_level: 'private' | 'restricted' | 'public';
    expires_at?: string;
}

export const documentsService = {
    /**
     * Fetches a list of documents for a user or based on filters.
     */
    async getDocuments(params?: {
        user_id?: string | number;
        page?: number;
        per_page?: number;
        search?: string;
        type?: string;
        employee?: string;
        access?: string;
    }): Promise<ApiResponse<{ documents: DocumentMetadata[]; pagination: any }>> {
        const response = await api.get('/hr/documents', { params });
        return response.data;
    },

    /**
     * Uploads a document (Blob).
     */
    async uploadDocument(formData: FormData): Promise<ApiResponse<{ message: string; document: any }>> {
        const response = await api.post('/hr/documents/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },

    /**
     * Downloads a document. Returns a Blob.
     */
    async downloadDocument(documentId: number): Promise<Blob> {
        const response = await api.get(`/hr/documents/${documentId}/download`, {
            responseType: 'blob',
        });
        return response.data;
    },

    /**
     * Deletes a document.
     */
    async deleteDocument(documentId: number): Promise<ApiResponse<{ message: string }>> {
        const response = await api.delete(`/hr/documents/${documentId}`);
        return response.data;
    },

    /**
     * Gets document types.
     */
    async getDocumentTypes(): Promise<ApiResponse<{ types: { value: string; label: string }[] }>> {
        const response = await api.get('/hr/documents/types');
        return response.data;
    },

    /**
     * Saves encryption recovery metadata.
     */
    async saveEncryptionMetadata(data: {
        encrypted_recovery_blob: string;
        recovery_salt: string;
        recovery_iv: string;
    }): Promise<ApiResponse<{ message: string }>> {
        const response = await api.post('/hr/documents/encryption-metadata', data);
        return response.data;
    },

    /**
     * Fetches encryption recovery metadata.
     */
    async getEncryptionMetadata(): Promise<ApiResponse<{
        encrypted_recovery_blob: string;
        recovery_salt: string;
        recovery_iv: string;
    }>> {
        const response = await api.get('/hr/documents/encryption-metadata');
        return response.data;
    },
};
